import React, { useState } from 'react';
import FeedbackCard from './FeedbackCard';
import FeedbackSelector from './FeedbackSelector';
import UnderwritingFeedbackTable from './UnderwritingFeedbackTable';
import ErrorDisplay, { hasErrors, getErrors } from '../Common/ErrorDisplay';
import { UNDERWRITING_CONFIG, getValueFromPath } from './config/underwritingFeedbackConfig';
import { submitFeedback, formatUnderwritingFeedback } from '../../utils/feedbackService';
import './UnderwritingFeedback.css';

/**
 * Main Underwriting Feedback Component
 * 
 * Displays risk assessment cards with feedback collection
 * Dynamically renders sections based on configuration and tool requirements
 */
const UnderwritingFeedback = ({ data, emailID, jobId, source }) => {
  // State management
  const [feedback, setFeedback] = useState({});
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [viewMode, setViewMode] = useState(UNDERWRITING_CONFIG.defaultView || 'table'); // Initialize from config

  // Show all sections (tool indicators will show execution status)
  const visibleSections = UNDERWRITING_CONFIG.sections;

  // Handle feedback change for a section
  const handleFeedbackChange = (sectionId, value) => {
    setFeedback(prev => ({
      ...prev,
      [sectionId]: value
    }));
  };

  // Validate all required feedback is provided AND comments field is filled
  const isFormValid = () => {
    // Check all required feedback is provided
    const allFeedbackProvided = visibleSections.every(section => {
      // Skip sections without feedback config
      if (!section.feedback) {
        return true;
      }
      if (section.feedback.required) {
        return feedback[section.id] !== undefined && feedback[section.id] !== null;
      }
      return true;
    });
    
    // Check comments field is not empty
    const commentsProvided = comments.trim().length > 0;
    
    return allFeedbackProvided && commentsProvided;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Button is already disabled until form is valid - no need to re-check
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Format payload using feedback service
      const payload = formatUnderwritingFeedback({
        leadId: data.lead_id,
        userId: emailID || 'unknown_user',
        jobId: jobId || `${emailID}_${Date.now()}`,
        doctype: 'abcd',
        feedback: feedback,
        feedbackConfig: UNDERWRITING_CONFIG.sections,
        comments: comments,
        source: source || 'WEB'
      });

      // Submit using feedback service
      const response = await submitFeedback(payload);
      
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="underwriting-feedback-empty">
        <p>No underwriting data available</p>
      </div>
    );
  }

  // Check if cam_output is blank and there are errors to display
  const showErrors = hasErrors(data);
  const errors = getErrors(data);

  // If cam_output is blank and there are errors, show error display
  if (showErrors) {
    return (
      <div className="underwriting-feedback-container">
        <div style={{ padding: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#333' }}>
            AI-Based Credit Decisioning
          </h3>
          <ErrorDisplay errors={errors} />
        </div>
      </div>
    );
  }

  // Render table view
  if (viewMode === 'table') {
    return (
      <div className="underwriting-feedback-container">
        <UnderwritingFeedbackTable
          data={data}
          emailID={emailID}
          jobId={jobId}
          source={source}
          feedback={feedback}
          onFeedbackChange={handleFeedbackChange}
          comments={comments}
          onCommentsChange={setComments}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submitSuccess={submitSuccess}
          onSubmit={handleSubmit}
        />
      </div>
    );
  }

  // Render card view
  return (
    <div className="underwriting-feedback-container">
      {/* Minimal header with timestamp only */}
      <div className="underwriting-header-minimal">
        <span className="timestamp">
          Generated: {new Date(data.timestamp).toLocaleString('en-IN')}
        </span>
      </div>

      {/* Scrollable cards area */}
      <div className="feedback-cards-scrollable">
        {visibleSections.map(sectionConfig => {
          const sectionData = getValueFromPath(data, sectionConfig.dataPath);
          
          return (
            <FeedbackCard
              key={sectionConfig.id}
              config={sectionConfig}
              data={sectionData}
              feedback={feedback[sectionConfig.id]}
              onFeedbackChange={(value) => handleFeedbackChange(sectionConfig.id, value)}
              allData={data}
            />
          );
        })}
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed-bottom-bar">
        <input
          type="text"
          className="comments-input"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder={UNDERWRITING_CONFIG.submit.commentsPlaceholder}
        />
        <button
          className="submit-icon-button"
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
          title="Submit Feedback"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <span>Submit</span>
              <span>➤</span>
            </>
          )}
        </button>
      </div>

      {/* Toast notifications */}
      {submitError && (
        <div className="submit-toast error">
          ❌ {submitError}
        </div>
      )}
      
      {submitSuccess && (
        <div className="submit-toast success">
          ✅ Feedback submitted successfully!
        </div>
      )}
    </div>
  );
};

export default UnderwritingFeedback;
